import { useMemo, useState } from 'react'
import { Check, X, Search } from 'lucide-react'
import type { NtArtifactOption, NtPresented, NtSchedule } from '../../core/types'
import { useAuth } from '../../core/auth'
import { useNorseTerm } from './NorseTermData'
import { usePresented, markPresented } from './model'
import { ArtifactList } from './ArtifactOptions'
import { Empty, ErrorState, Loading } from '../../ui/States'

/**
 * The open house checklist: every student, what their artifact is, whether
 * it comes from a course or their independent project, and whether they
 * presented. Office and admin mark it walking the floor, on a phone or at a
 * desk. "Did not present" is a real answer, kept apart from "not checked".
 *
 * Built from the student schedules placement writes, so it is everyone who
 * was placed, and from the approved proposals for project artifacts.
 */
interface Row {
  s: NtSchedule
  source: 'project' | 'course' | 'none' | 'exempt'
  /** The project title, or the course and its teacher. */
  where: string
  room?: string
  /** A project's own description of its artifact. */
  text?: string
  /** A course's artifact options. */
  options?: NtArtifactOption[]
  mark?: NtPresented
}

type Show = 'all' | 'unmarked' | 'yes' | 'no'

export function OpenHouse() {
  const { profile, user } = useAuth()
  const { term, schedules, proposals, roles } = useNorseTerm()
  const marks = usePresented(term.id, true)
  const [show, setShow] = useState<Show>('all')
  const [where, setWhere] = useState('')
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const may = roles.run || roles.attend
  const who = profile?.displayName ?? user?.email ?? ''

  const rows = useMemo<Row[]>(() => {
    const markOf = new Map((marks ?? []).map((m) => [m.studentId, m]))
    const proposalOf = (s: NtSchedule) => proposals.find((p) => p.status === 'approved'
      && (p.studentId === s.studentId || (!!s.studentEmail && p.studentEmail === s.studentEmail)))
    return schedules.map((s): Row => {
      const mark = markOf.get(s.studentId)
      if (s.independent) {
        const p = proposalOf(s)
        return { s, mark, source: 'project', where: s.independentTitle || p?.title || 'Independent project', text: p?.artifact }
      }
      if (s.noArtifact) return { s, mark, source: 'exempt', where: `${s.amTitle ?? 'Credit recovery'}, all day` }
      const b = s.artifactBlock
      if (b === 'AM' || b === 'PM') {
        const am = b === 'AM'
        return { s, mark, source: 'course',
          where: `${(am ? s.amTitle : s.pmTitle) ?? 'Course'}${(am ? s.amTeacherName : s.pmTeacherName) ? `, ${am ? s.amTeacherName : s.pmTeacherName}` : ''}`,
          room: am ? s.amRoom : s.pmRoom, options: am ? s.amArtifacts : s.pmArtifacts }
      }
      return { s, mark, source: 'none', where: 'Artifact course not chosen yet' }
    }).sort((a, b) => a.s.name.localeCompare(b.s.name))
  }, [schedules, proposals, marks])

  if (marks === null) return <Loading label="Loading the open house checklist" />
  if (!schedules.length) {
    return <Empty title="Nobody to check yet" body="The checklist fills in once placement has run and students have schedules." />
  }

  const places = [...new Set(rows.map((r) => (r.source === 'project' ? 'Independent projects' : r.where)))].sort()
  const shown = rows
    .filter((r) => show === 'all' || (show === 'unmarked' ? !r.mark && r.source !== 'exempt' : show === 'yes' ? r.mark?.presented === true : r.mark?.presented === false))
    .filter((r) => !where || (where === 'Independent projects' ? r.source === 'project' : r.where === where))
    .filter((r) => !q || `${r.s.name} ${r.where} ${r.text ?? ''}`.toLowerCase().includes(q.toLowerCase()))
  const count = { yes: rows.filter((r) => r.mark?.presented === true).length, no: rows.filter((r) => r.mark?.presented === false).length }
  // Credit recovery all day makes no artifact: listed, never counted as unchecked.
  const presenters = rows.filter((r) => r.source !== 'exempt').length
  const unmarked = presenters - count.yes - count.no

  const set = (r: Row, v: boolean | null) => async () => {
    setBusy(r.s.studentId); setErr(null)
    try { await markPresented(term.id, r.s.studentId, r.mark?.presented === v ? null : v, r.mark?.note ?? '', who) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(null) }
  }

  const marker = (r: Row) => r.source === 'exempt' ? <span className="work__sub">No artifact</span> : (
    <div className="oh__marks" role="group" aria-label={`Did ${r.s.name} present?`}>
      <button type="button" className={`oh__mark${r.mark?.presented === true ? ' is-yes' : ''}`} aria-pressed={r.mark?.presented === true}
        disabled={!may || busy === r.s.studentId} onClick={set(r, true)}>
        <Check size={15} aria-hidden="true" /> Presented
      </button>
      <button type="button" className={`oh__mark${r.mark?.presented === false ? ' is-no' : ''}`} aria-pressed={r.mark?.presented === false}
        disabled={!may || busy === r.s.studentId} onClick={set(r, false)}>
        <X size={15} aria-hidden="true" /> Did not
      </button>
    </div>
  )
  const source = (r: Row) => (
    <span className={`pill${r.source === 'none' ? ' pill--serious' : ''}`}>
      {r.source === 'project' ? 'Project' : r.source === 'course' ? 'Course' : r.source === 'exempt' ? 'No artifact' : 'Not chosen'}
    </span>
  )
  const artifact = (r: Row) => r.source === 'project'
    ? (r.text?.trim() ? <span style={{ whiteSpace: 'pre-wrap' }}>{r.text}</span> : <span className="work__sub">Not described</span>)
    : r.source === 'course' ? <ArtifactList options={r.options} compact />
    : r.source === 'exempt' ? <span className="work__sub">None: credit recovery all day</span> : <span className="work__sub">Not chosen</span>

  return (
    <>
      <p className="ov__p" style={{ marginTop: 'var(--s-2)' }}>
        <strong>{count.yes}</strong> presented · <strong>{count.no}</strong> did not · <strong>{unmarked}</strong> not checked yet,
        of {presenters} students{presenters < rows.length ? ` (${rows.length - presenters} in credit recovery all day make no artifact)` : ''}. Tap again to clear a mark.
      </p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div className="filters">
        <div className="chips" role="tablist" aria-label="Show">
          {([['all', `All (${rows.length})`], ['unmarked', `Not checked (${unmarked})`], ['yes', `Presented (${count.yes})`], ['no', `Did not (${count.no})`]] as const).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={show === k} className={`chip${show === k ? ' is-on' : ''}`} onClick={() => setShow(k)}>{label}</button>
          ))}
        </div>
        <select className="opt__input" style={{ maxWidth: 280 }} value={where} aria-label="Course or project" onChange={(e) => setWhere(e.target.value)}>
          <option value="">Every course and project</option>
          {places.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="sel__search">
          <Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Search" aria-label="Search students" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>

      {shown.length === 0 ? <p className="muted-note">Nobody matches.</p> : (
        <>
          <table className="table">
            <thead>
              <tr><th>Student</th><th>From</th><th>Course or project</th><th>Artifact</th><th>Presented?</th></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.s.studentId}>
                  <td><div style={{ fontWeight: 610 }}>{r.s.name}</div><div className="work__sub">Grade {r.s.grade}</div></td>
                  <td>{source(r)}</td>
                  <td>{r.where}{r.room ? <div className="work__sub">{r.room}</div> : null}</td>
                  <td style={{ maxWidth: 320 }}>{artifact(r)}</td>
                  <td>{marker(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.s.studentId} className="work__row" style={{ display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
                  <div style={{ fontWeight: 610 }}>{r.s.name}</div>{source(r)}
                </div>
                <div className="work__sub">{r.where}{r.room ? ` · ${r.room}` : ''}</div>
                <div style={{ margin: 'var(--s-1) 0 var(--s-2)', fontSize: 'var(--t-sm)' }}>{artifact(r)}</div>
                {marker(r)}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
